// client/src/components/pages/LexiStar/Replay/trips/TripsView.tsx
// The Trips tab of Replay (ST-00318; until then the Geofences page's Trips tab, ST-00159,
// converted from .js in ST-00216). MapLibre GL on the shared LexiStarMap foundation (ST-00118).
//
// The DAY VIEW: every trip of the chosen day or date range on the map at once, for every
// vehicle or the ones picked, each trip in its own colour with its number at its start and a
// ring at its end, and the day's stops faint. The list beside it groups the trips by vehicle;
// pointing at a row highlights its trip on the map and pointing at a line highlights its row.
// Checkboxes switch trips and vehicles on and off. One read for the whole fleet
// (GET /api/vehicle-locations/trips, geofencing.view) with simplified lines, drawn as GeoJSON
// layers (./TripsDayLayers).
//
// ST-00318 — one player: clicking a trip (its row or its line) opens it in Replay itself,
// /lexistar/replay?asset_key=reg:<PLATE>&from=<start>&to=<end> (../replayPaths), and the tab's
// own small player (speed line, A / B, playback at 1x/4x/10x) is retired. Before leaving, the
// trip becomes this view's selected trip (?trip=) on the current history entry, and the replay
// gets the way back in its navigation state: the browser's Back, and Replay's "Back to trips",
// land on the tab as it was with that trip marked. Without Replay's permission
// (lexistar.replay.view) a click selects the trip and says what playing it back needs.
//
// The view is in the address (?vehicle=, ?from=, ?to=, ?trip=, ?hide=, ?hideVehicle=;
// ./tripsDay parse/write them), so a link reproduces it. On a phone the list comes first,
// with a List / Map switch.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Popup } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent } from 'react-map-gl/maplibre';
import type { LngLatBoundsLike, Map as MaplibreMap } from 'maplibre-gl';
import type { Point } from 'geojson';
import { boundsFromLngLats as boundsFromLngLatsJs } from '../../../../map/geo';
import api from '../../../../../services/api';
import {
  LexiStarMap,
  SegmentedControl,
  ThemedMultiSelect,
  UnifiedDateRangePicker,
} from '../../../../../types/commonComponents';
import {
  toApiDateString,
  formatMiningDateShort,
  formatMiningTime,
} from '../../../../../utils/dateUtils';
import { FaRoute, FaExpand, FaList, FaMap, FaPlay, FaTimes } from 'react-icons/fa';
import type {
  FleetStop,
  FleetTripsResponse,
  FleetTripsResult,
  LiveVehiclesResponse,
  TelemetryDevicesResponse,
} from '../../../../../types/clientPages/geofencing';
import TripsDayList, { TripSwatch } from './TripsDayList';
import TripsDayLayers, {
  DAY_INTERACTIVE_LAYER_IDS,
  DAY_STOP_LAYER_ID,
  DAY_TRIP_LAYER_IDS,
} from './TripsDayLayers';
import { addMissingTripBadge } from './tripMapImages';
import { tripReplayPath, type ReplayNavState } from '../replayPaths';
import {
  ALL_VISIBLE,
  type Basemap,
  type DayTrip,
  type DayVehicle,
  type TripsUrlState,
  buildTripsDay,
  dayToDate,
  formatDuration,
  fromTo,
  hideAll,
  isTripVisible,
  parseTripsParams,
  toggleTrip,
  toggleVehicle,
  tripColor,
  tripsCoords,
  writeTripsParams,
} from './tripsDay';

// Typed view of the JavaScript map helper (components/map/geo.js).
const boundsFromLngLats = boundsFromLngLatsJs as unknown as (
  points: [number, number][] | null
) => LngLatBoundsLike | null;

interface HoveredStop {
  key: string;
  lng: number;
  lat: number;
  text: string;
}

/** The trip pointed at: from its list row, or from its line / badge on the map (with where). */
interface HoveredTrip {
  key: string;
  fromMap: boolean;
  lng?: number;
  lat?: number;
}

interface StopFeatureProps {
  key: string;
  text: string;
}

export interface TripsViewProps {
  /**
   * The viewer may use Replay (lexistar.replay.view): a click replays the trip there. Without
   * it a click selects the trip (the small player that played it here is retired, ST-00318).
   */
  canReplay: boolean;
}

// Centred on South Africa; fitBounds handles the real framing once data loads.
const DEFAULT_VIEW = { longitude: 24.6727, latitude: -28.4793, zoom: 6 };
/** The picker sets the start and then the end of a range: load once, for the range. */
const FETCH_DEBOUNCE_MS = 120;
const LOAD_ERROR = 'Failed to load trips. Please try again.';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Operational-day window → ISO datetimes for the API. All times in this app
// render in Africa/Johannesburg (formatMiningTime), so the query window must
// be the SAST day regardless of the browser's timezone. SAST is UTC+2 with no
// DST, so a fixed offset is correct.
const dayStartIso = (day: string): string => new Date(`${day}T00:00:00+02:00`).toISOString();
const dayEndIso = (day: string): string => new Date(`${day}T23:59:59.999+02:00`).toISOString();

const stopText = (s: FleetStop): string =>
  `${s.regNo} · ${s.durationMin >= 60 ? 'Parked' : 'Stopped'} ${formatDuration(s.durationMin)} · ${formatMiningTime(s.startTs)} – ${formatMiningTime(s.endTs)}${s.zone ? ` · ${s.zone.name}` : ''}`;

/** The server's message for a window that is too big (422), else the generic one. */
function loadErrorMessage(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: { error?: unknown } } } | null)
    ?.response;
  return res?.status === 422 && typeof res.data?.error === 'string' ? res.data.error : LOAD_ERROR;
}

/** The trip-badge images are drawn on demand; register the handler once per map instance. */
const badgeHandlerMaps = new WeakSet<MaplibreMap>();
function registerBadgeImages(map: MaplibreMap): void {
  if (badgeHandlerMaps.has(map)) return;
  badgeHandlerMaps.add(map);
  map.on('styleimagemissing', (e: { id: string }) => addMissingTripBadge(map, e.id));
}

/**
 * The selected trip, above the list: what it is, and either "Replay this trip" or what playing
 * it back needs; the cross clears the selection.
 */
function SelectedTrip({
  trip,
  basemap,
  multiDay,
  canReplay,
  onReplay,
  onClear,
}: {
  trip: DayTrip;
  basemap: Basemap;
  multiDay: boolean;
  canReplay: boolean;
  onReplay: () => void;
  onClear: () => void;
}) {
  return (
    <section
      aria-label="Selected trip"
      className="bg-surface rounded-lg border border-teal-500 ring-1 ring-teal-500 p-3"
    >
      <div className="flex items-center gap-2 min-w-0">
        <TripSwatch trip={trip} basemap={basemap} />
        <h3 className="text-sm font-semibold text-slate-800 truncate">
          Trip {trip.n} · {trip.regNo}
        </h3>
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear the selected trip"
          title="Clear the selected trip"
          className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <FaTimes size={12} aria-hidden="true" />
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-600 tabular-nums">
        {multiDay && `${formatMiningDateShort(trip.startTs)} `}
        {formatMiningTime(trip.startTs)} – {formatMiningTime(trip.endTs)} ·{' '}
        {formatDuration(trip.durationMin)} · {(trip.distanceKm || 0).toFixed(1)} km
      </p>
      <p className="mt-0.5 text-xs text-slate-600 truncate" title={fromTo(trip)}>
        {fromTo(trip)}
      </p>
      {canReplay ? (
        <button
          type="button"
          onClick={onReplay}
          className="mt-2 inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          <FaPlay size={11} aria-hidden="true" />
          Replay this trip
        </button>
      ) : (
        <p className="mt-2 text-xs text-slate-600">
          Playing a trip back needs permission to use Replay. Ask your administrator.
        </p>
      )}
    </section>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function TripsView({ canReplay }: TripsViewProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // ── The view, in the address ──
  const [params, setParams] = useSearchParams();
  const url = useMemo(() => parseTripsParams(params), [params]);
  // The latest view, also between a change and its render (the picker sets start, then end).
  const urlRef = useRef<TripsUrlState>(url);
  useEffect(() => {
    urlRef.current = url;
  }, [url]);
  const updateUrl = useCallback(
    (patch: Partial<TripsUrlState>) => {
      const next = { ...urlRef.current, ...patch };
      urlRef.current = next;
      setParams((prev) => writeTripsParams(prev, next), { replace: true });
    },
    [setParams]
  );

  const [todayDay] = useState(() => toApiDateString(new Date()));
  const fromDay = url.from ?? todayDay;
  const toCandidate = url.to ?? todayDay;
  const toDay = toCandidate < fromDay ? fromDay : toCandidate;
  const startDate = useMemo(() => dayToDate(fromDay) ?? new Date(), [fromDay]);
  const endDate = useMemo(() => dayToDate(toDay) ?? new Date(), [toDay]);
  const multiDay = fromDay !== toDay;
  const vehiclesKey = url.vehicles.join('\n');

  const [vehicleOptions, setVehicleOptions] = useState<string[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const [dayData, setDayData] = useState<FleetTripsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Map / list UI state
  const [mapReady, setMapReady] = useState(false);
  const [basemap, setBasemap] = useState<Basemap>('street');
  const [pane, setPane] = useState<'list' | 'map'>('list'); // phones only
  const [hover, setHover] = useState<HoveredTrip | null>(null);
  const [hoveredStop, setHoveredStop] = useState<HoveredStop | null>(null); // { key, lng, lat, text }
  const [fitNonce, setFitNonce] = useState(0);

  // Raw maplibre instance.
  const glRef = useRef<MaplibreMap | null>(null);

  // ── Vehicle options: live positions merged with the device registry ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [liveRes, devRes] = await Promise.allSettled([
        api.vehicleLocations.getLive(),
        api.telemetry.getDevices(),
      ]);
      if (cancelled) return;
      const regNos = new Set<string>();
      if (devRes.status === 'fulfilled') {
        ((devRes.value.data as TelemetryDevicesResponse | undefined)?.devices || []).forEach(
          (d) => {
            if (d.vehicle_reg_no) regNos.add(d.vehicle_reg_no);
          }
        );
      }
      if (liveRes.status === 'fulfilled') {
        ((liveRes.value.data as LiveVehiclesResponse | undefined)?.data || []).forEach((v) => {
          if (v.regNo) regNos.add(v.regNo);
        });
      }
      setVehicleOptions(Array.from(regNos).sort());
      setOptionsLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Every vehicle's trips for the window (one read for the fleet) ──
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const res = await api.vehicleLocations.getFleetTrips(
          dayStartIso(fromDay),
          dayEndIso(toDay),
          vehiclesKey ? vehiclesKey.split('\n') : undefined
        );
        if (cancelled) return;
        setDayData(
          (res.data as FleetTripsResponse | undefined)?.data ?? {
            vehicles: [],
            tripCount: 0,
            pointCount: 0,
          }
        );
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching trips:', err);
        setError(loadErrorMessage(err));
        setDayData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, FETCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fromDay, toDay, vehiclesKey, reloadNonce]);

  // ── Derived data ──
  const day = useMemo(() => buildTripsDay(dayData), [dayData]);
  const visibility = url.visibility;
  const selected: DayTrip | null = url.trip ? (day.byKey.get(url.trip) ?? null) : null;
  const selectedKey = selected?.key ?? null;

  const visibleTrips = useMemo(
    () => day.trips.filter((t) => isTripVisible(t, visibility)),
    [day, visibility]
  );
  const visibleStops = useMemo(
    () =>
      day.vehicles.filter((v) => !visibility.hiddenVehicles.has(v.regNo)).flatMap((v) => v.stops),
    [day, visibility]
  );

  // A ?trip= that this window does not have: no trip selected.
  useEffect(() => {
    if (!loading && dayData && url.trip && !day.byKey.has(url.trip)) updateUrl({ trip: null });
  }, [loading, dayData, day, url.trip, updateUrl]);

  // What the map frames ([lng,lat]): the trips switched on, else the stops.
  const fitCoords = useMemo((): [number, number][] | null => {
    if (visibleTrips.length) return tripsCoords(visibleTrips);
    if (visibleStops.length) return visibleStops.map((s) => [s.lng, s.lat]);
    return null;
  }, [visibleTrips, visibleStops]);
  const fitCoordsRef = useRef(fitCoords);
  useEffect(() => {
    fitCoordsRef.current = fitCoords;
  }, [fitCoords]);

  // ── Map load: capture the instance and draw the trip badges on demand ──
  const handleLoad = useCallback((e: { target: MaplibreMap }) => {
    glRef.current = e.target;
    registerBadgeImages(e.target);
    setMapReady(true);
  }, []);

  // ── fitBounds: on new data, the Fit button, showing the map ──
  useEffect(() => {
    if (!mapReady) return;
    const map = glRef.current;
    if (!map) return;
    const bounds = boundsFromLngLats(fitCoordsRef.current);
    if (!bounds) return;
    try {
      map.resize(); // the phone's map pane may just have become visible
      map.fitBounds(bounds, { padding: 48, duration: 500, maxZoom: 16 });
    } catch {
      /* map disposed */
    }
  }, [mapReady, dayData, fitNonce, pane]);

  // ── Pointer over the map: trips and stops ──
  const handleMouseMove = useCallback((e: MapLayerMouseEvent) => {
    const f = e.features && e.features[0];
    const layerId = f?.layer.id;
    if (f && layerId === DAY_STOP_LAYER_ID) {
      const [lng, lat] = (f.geometry as Point).coordinates as [number, number];
      const { key, text } = f.properties as StopFeatureProps;
      setHoveredStop((prev) => (prev && prev.key === key ? prev : { key, lng, lat, text }));
      setHover((prev) => (prev?.fromMap ? null : prev));
      return;
    }
    setHoveredStop((prev) => (prev ? null : prev));
    if (f && layerId && DAY_TRIP_LAYER_IDS.includes(layerId)) {
      const { key } = f.properties as { key: string };
      setHover((prev) =>
        prev && prev.fromMap && prev.key === key
          ? prev
          : { key, fromMap: true, lng: e.lngLat.lng, lat: e.lngLat.lat }
      );
    } else {
      setHover((prev) => (prev?.fromMap ? null : prev));
    }
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHoveredStop((prev) => (prev ? null : prev));
    setHover((prev) => (prev?.fromMap ? null : prev));
  }, []);

  // ── Actions ──
  /**
   * A trip clicked (row, line or badge): replayed in Replay — after making it this entry's
   * selected trip, so Back (or Replay's "Back to trips") lands on the tab as it is, with the
   * trip marked. Without Replay's permission: selected here.
   */
  const openTrip = useCallback(
    (key: string) => {
      setHover(null);
      setHoveredStop(null);
      const trip = day.byKey.get(key);
      if (!trip) return;
      if (!canReplay) {
        updateUrl({ trip: key });
        return;
      }
      urlRef.current = { ...urlRef.current, trip: key };
      const here = `${pathname}?${writeTripsParams(params, urlRef.current)}`;
      navigate(here, { replace: true });
      const state: ReplayNavState = { tripsBack: here };
      navigate(tripReplayPath(trip), { state });
    },
    [canReplay, day, navigate, params, pathname, updateUrl]
  );
  const clearSelection = useCallback(() => updateUrl({ trip: null }), [updateUrl]);
  const handleMapClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const f = e.features && e.features[0];
      if (f && DAY_TRIP_LAYER_IDS.includes(f.layer.id)) {
        openTrip((f.properties as { key: string }).key);
      }
    },
    [openTrip]
  );
  const onListHighlight = useCallback(
    (key: string | null) => setHover(key ? { key, fromMap: false } : null),
    []
  );
  const onToggleTrip = useCallback(
    (trip: DayTrip, vehicle: DayVehicle) =>
      updateUrl({ visibility: toggleTrip(urlRef.current.visibility, trip, vehicle) }),
    [updateUrl]
  );
  const onToggleVehicle = useCallback(
    (vehicle: DayVehicle) =>
      updateUrl({ visibility: toggleVehicle(urlRef.current.visibility, vehicle) }),
    [updateUrl]
  );
  const onShowAll = useCallback(() => updateUrl({ visibility: ALL_VISIBLE }), [updateUrl]);
  const onHideAll = useCallback(() => updateUrl({ visibility: hideAll(day) }), [updateUrl, day]);
  const onStartDateChange = useCallback(
    (d: Date) => {
      const from = toApiDateString(d);
      const to = urlRef.current.to ?? todayDay;
      updateUrl({ from, to: to < from ? from : to, trip: null });
    },
    [updateUrl, todayDay]
  );
  const onEndDateChange = useCallback(
    (d: Date) =>
      updateUrl({ from: urlRef.current.from ?? todayDay, to: toApiDateString(d), trip: null }),
    [updateUrl, todayDay]
  );

  // The trip pointed at, else the trip selected, is drawn on top with the rest faded.
  const highlightKey = hover?.key ?? selectedKey;
  const hoveredTrip = hover?.fromMap ? (day.byKey.get(hover.key) ?? null) : null;
  // Vehicles in the address that the registry does not list still show as picked.
  const pickerOptions = useMemo(
    () => [...new Set([...vehicleOptions, ...url.vehicles])].sort(),
    [vehicleOptions, url.vehicles]
  );
  const noVehicles =
    optionsLoaded && vehicleOptions.length === 0 && !loading && !error && day.vehicles.length === 0;
  const noTrips = !loading && !error && day.trips.length === 0;

  const listColumn = loading ? (
    <div className="bg-surface rounded-lg border border-slate-200 py-16 flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mb-3" />
      <p className="text-xs text-slate-400">Loading trips...</p>
    </div>
  ) : error ? null : noTrips ? (
    <div className="bg-surface rounded-lg border border-slate-200 py-16 px-4 text-center">
      <FaRoute className="mx-auto text-slate-300 mb-3" size={36} />
      <p className="text-sm font-medium text-slate-600">
        No trips recorded for this {multiDay ? 'period' : 'day'}
      </p>
      <p className="text-xs text-slate-400 mt-1">
        {visibleStops.length > 0
          ? 'The vehicles stood still: their stops are on the map. Try another date or vehicle.'
          : 'Try another date or vehicle.'}
      </p>
    </div>
  ) : (
    <div className="space-y-2">
      {selected && (
        <SelectedTrip
          trip={selected}
          basemap={basemap}
          multiDay={multiDay}
          canReplay={canReplay}
          onReplay={() => openTrip(selected.key)}
          onClear={clearSelection}
        />
      )}
      <TripsDayList
        day={day}
        visibility={visibility}
        basemap={basemap}
        multiDay={multiDay}
        highlightKey={highlightKey}
        highlightFromMap={!!hover?.fromMap}
        selectedKey={selectedKey}
        rowAction={canReplay ? 'replay' : 'select'}
        onHighlight={onListHighlight}
        onSelect={openTrip}
        onToggleTrip={onToggleTrip}
        onToggleVehicle={onToggleVehicle}
        onShowAll={onShowAll}
        onHideAll={onHideAll}
      />
    </div>
  );

  return (
    <div>
      {/* Controls row */}
      <div className="bg-surface rounded-lg border border-slate-200 p-3 mb-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="w-full sm:w-80">
          <label
            htmlFor="trips-vehicles"
            className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5"
          >
            Vehicles
          </label>
          <ThemedMultiSelect
            id="trips-vehicles"
            isMulti
            isClearable
            value={url.vehicles}
            placeholder="All vehicles"
            onChange={(e) => updateUrl({ vehicles: e.target.value.map(String), trip: null })}
            className="text-sm"
          >
            {pickerOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </ThemedMultiSelect>
        </div>
        <div className="w-full sm:w-64">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Date range
          </label>
          <UnifiedDateRangePicker
            startDate={startDate}
            onStartDateChange={onStartDateChange}
            endDate={endDate}
            onEndDateChange={onEndDateChange}
          />
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={() => setReloadNonce((n) => n + 1)}
            className="font-semibold text-red-700 hover:text-red-800 hover:underline flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {noVehicles ? (
        <div className="bg-surface rounded-lg border border-slate-200 py-16 text-center">
          <FaRoute className="mx-auto text-slate-300 mb-3" size={36} />
          <p className="text-sm font-medium text-slate-600">No vehicles available</p>
          <p className="text-xs text-slate-400 mt-1">
            Register a GPS device to see trip history here.
          </p>
        </div>
      ) : (
        <>
          {/* Phones: the list first, the map one tap away */}
          <SegmentedControl
            ariaLabel="Show the trips as"
            className="lg:hidden mb-3"
            value={pane}
            onChange={setPane}
            options={[
              { value: 'list', label: 'List', icon: FaList },
              { value: 'map', label: 'Map', icon: FaMap },
            ]}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* ── Trip list column ── */}
            <div className={`lg:col-span-1 ${pane === 'list' ? '' : 'hidden lg:block'}`}>
              {listColumn}
            </div>

            {/* ── Map column ── */}
            <div className={`lg:col-span-2 ${pane === 'map' ? '' : 'hidden lg:block'}`}>
              {/* z-0 keeps the map's stacking context under the mobile sidebar
                  drawer (z-60). See ST-00098 / LiveTrackingMap. */}
              <div className="relative z-0 h-[460px] lg:h-[600px] rounded-lg overflow-hidden border border-gray-200">
                <LexiStarMap
                  className="absolute inset-0"
                  initialViewState={DEFAULT_VIEW}
                  maxZoom={22}
                  basemap={basemap}
                  onBasemapChange={setBasemap}
                  basemapTogglePosition="bottom-left"
                  navigationPosition="top-left"
                  interactiveLayerIds={DAY_INTERACTIVE_LAYER_IDS}
                  onLoad={handleLoad}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onClick={handleMapClick}
                  overlay={
                    fitCoords ? (
                      <button
                        type="button"
                        onClick={() => setFitNonce((n) => n + 1)}
                        className="absolute top-3 right-3 z-[1000] inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-surface/95 backdrop-blur shadow-lg text-xs font-medium text-slate-700 hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      >
                        <FaExpand size={11} aria-hidden="true" />
                        Fit to visible trips
                      </button>
                    ) : null
                  }
                >
                  {/* Every visible trip, the stops faint */}
                  <TripsDayLayers
                    trips={visibleTrips}
                    stops={visibleStops}
                    basemap={basemap}
                    highlightKey={highlightKey}
                    describeStop={stopText}
                  />

                  {/* Trip hover popup (pointing at a line or badge) */}
                  {hoveredTrip && hover?.lng != null && hover.lat != null && (
                    <Popup
                      longitude={hover.lng}
                      latitude={hover.lat}
                      offset={12}
                      closeButton={false}
                      closeOnClick={false}
                      maxWidth="260px"
                    >
                      <div className="text-xs text-slate-700">
                        <p className="font-semibold">
                          Trip {hoveredTrip.n} · {hoveredTrip.regNo}
                        </p>
                        <p className="tabular-nums">
                          {formatMiningTime(hoveredTrip.startTs)} –{' '}
                          {formatMiningTime(hoveredTrip.endTs)} ·{' '}
                          {(hoveredTrip.distanceKm || 0).toFixed(1)} km
                        </p>
                        <p className="text-slate-500">{fromTo(hoveredTrip)}</p>
                        <p className="text-slate-500 mt-0.5">
                          {canReplay ? 'Click to replay it' : 'Click to select it'}
                        </p>
                      </div>
                    </Popup>
                  )}

                  {/* Stop hover popup */}
                  {hoveredStop && (
                    <Popup
                      longitude={hoveredStop.lng}
                      latitude={hoveredStop.lat}
                      anchor="bottom"
                      offset={14}
                      closeButton={false}
                      closeOnClick={false}
                      maxWidth="260px"
                    >
                      <div className="text-xs font-medium text-slate-700">{hoveredStop.text}</div>
                    </Popup>
                  )}
                </LexiStarMap>

                {loading && (
                  <div
                    role="status"
                    className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-surface/95 backdrop-blur shadow-lg text-xs font-medium text-slate-600"
                  >
                    <span
                      className="inline-block w-3 h-3 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"
                      aria-hidden="true"
                    />
                    Loading trips...
                  </div>
                )}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-2 px-1">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold text-white"
                    style={{ background: tripColor(1, basemap) }}
                  >
                    1
                  </span>
                  Each colour is one trip; its number marks the start
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-block w-3 h-3 rounded-full bg-white"
                    style={{ boxShadow: `inset 0 0 0 2.5px ${tripColor(1, basemap)}` }}
                  />
                  the ring its end
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-block w-2.5 h-2.5 rounded-full bg-slate-400 opacity-70"
                  />
                  stops
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
